import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { messagesAPI, purchasesAPI } from '../api';
import { liteClient } from 'algoliasearch/lite';
import { formatStoredDeliveryLabel, loadHomeDeliveryLocation, saveDeliveryLocationFromModal } from '../utils/homeLocationStorage';
import Logo from './Logo';
import ListingImage from './ListingImage';

const HomeLocationModal = lazy(() => import('./HomeLocationModal'));

const searchClient = liteClient('NC39OBLGG3', 'dc77e2a6d622659fd216427832b00f1d');

interface SearchResult {
  objectID: string;
  id: number;
  title: string;
  price: number;
  image_url?: string;
  category?: string;
}

function shouldShowNavDelivery(pathname: string): boolean {
  if (pathname === '/search') return true;
  if (!pathname.startsWith('/listings/')) return false;
  const id = pathname.slice('/listings/'.length).split('/')[0];
  return /^\d+$/.test(id);
}

type NavDeliveryChipProps = {
  label: string;
  onClick: () => void;
  className?: string;
};

function NavDeliveryChip({ label, onClick, className = '' }: NavDeliveryChipProps) {
  const shared = label
    ? 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100/80'
    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label ? `Delivery address: ${label}. Change location` : 'Set delivery location'}
      title={label || undefined}
      className={`inline-flex w-auto max-w-[10rem] sm:max-w-[13rem] md:max-w-[16rem] lg:max-w-[20rem] min-w-0 min-h-[36px] sm:min-h-[40px] items-center gap-1.5 sm:gap-2 rounded-lg border px-2 py-1.5 sm:px-3.5 sm:py-2 text-left transition-colors touch-manipulation ${shared} ${className}`}
    >
      {label ? (
        <>
          <i className="ri-map-pin-2-fill shrink-0 text-sm text-green-600" aria-hidden />
          <span className="min-w-0 truncate text-xs sm:text-sm font-semibold text-gray-900">{label}</span>
        </>
      ) : (
        <>
          <i className="ri-map-pin-2-line shrink-0 text-sm text-green-600" aria-hidden />
          <span className="min-w-0 truncate text-xs sm:text-sm font-medium">
            Where to deliver?
          </span>
        </>
      )}
    </button>
  );
}

const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { getTotalItems } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [deliveredOrdersCount, setDeliveredOrdersCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showMobileOverlay, setShowMobileOverlay] = useState(false);
  const [deliveryLabel, setDeliveryLabel] = useState(() => formatStoredDeliveryLabel(loadHomeDeliveryLocation()));
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isSearchWrapperActive, setIsSearchWrapperActive] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  /** Skip toast on first unread poll; then notify when count increases */
  const prevUnreadRef = useRef<number | null>(null);
  const [mobileBottomNavVisible, setMobileBottomNavVisible] = useState(true);
  const lastScrollYRef = useRef(0);

  const accountAlertCount = unreadMessagesCount;

  const handleLogout = () => {
    logout();
    navigate('/');
    setDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setDropdownOpen(false);
      }
      
      // For search dropdown, check if click is outside both the dropdown and the input
      if (searchDropdownRef.current) {
        const isClickInsideDropdown = searchDropdownRef.current.contains(target);
        const isClickOnInput = searchInputRef.current?.contains(target);
        
        if (!isClickInsideDropdown && !isClickOnInput) {
          setShowSearchDropdown(false);
        }
      }
      
      // Collapse search wrapper when clicking outside
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(target)) {
        setIsSearchWrapperActive(false);
        setIsSearchExpanded(false);
        setSearchQuery('');
        setShowSearchDropdown(false);
      }
    };

    if (dropdownOpen || showSearchDropdown || isSearchWrapperActive) {
      // Use a small delay to allow click events on dropdown items to fire first
      setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, showSearchDropdown, isSearchWrapperActive]);

  // Mobile bottom tab bar: hide on scroll down, show on scroll up
  useEffect(() => {
    if (!isAuthenticated) return;

    const SCROLL_DELTA = 8;
    const TOP_SHOW = 16;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (window.innerWidth >= 640) {
          setMobileBottomNavVisible(true);
          ticking = false;
          return;
        }
        const y = window.scrollY;
        if (y <= TOP_SHOW) {
          setMobileBottomNavVisible(true);
        } else if (y > lastScrollYRef.current + SCROLL_DELTA) {
          setMobileBottomNavVisible(false);
        } else if (y < lastScrollYRef.current - SCROLL_DELTA) {
          setMobileBottomNavVisible(true);
        }
        lastScrollYRef.current = y;
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isAuthenticated]);

  useEffect(() => {
    setMobileBottomNavVisible(true);
    lastScrollYRef.current = window.scrollY;
  }, [location.pathname]);

  useEffect(() => {
    const refreshDeliveryLabel = () => {
      setDeliveryLabel(formatStoredDeliveryLabel(loadHomeDeliveryLocation()));
    };
    refreshDeliveryLabel();
    window.addEventListener('delivery-location-updated', refreshDeliveryLabel);
    window.addEventListener('storage', refreshDeliveryLabel);
    return () => {
      window.removeEventListener('delivery-location-updated', refreshDeliveryLabel);
      window.removeEventListener('storage', refreshDeliveryLabel);
    };
  }, [location.pathname]);

  // Get search results
  useEffect(() => {
    const getResults = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSearchResults([]);
        setShowSearchDropdown(false);
        return;
      }

      // Show dropdown immediately when typing
      setShowSearchDropdown(true);
      
      try {
        const response = await searchClient.search({
          requests: [
            {
              indexName: 'listings',
              query: searchQuery,
              params: {
                hitsPerPage: 8,
                attributesToRetrieve: ['id', 'title', 'price', 'image_url', 'category', 'description', 'store'],
                attributesToHighlight: ['title', 'description'],
              } as any,
            } as any,
          ],
        });

        const firstResult = (response.results || [])[0] as any;
        if (!firstResult) {
          setSearchResults([]);
          setShowSearchDropdown(true);
          return;
        }

        const hits = firstResult.hits || [];
        const results: SearchResult[] = hits.map((hit: any) => {
          let title = hit.title || '';
          if (hit._highlightResult?.title?.value) {
            title = hit._highlightResult.title.value;
          }
          return {
            objectID: hit.objectID || hit.id?.toString() || '',
            id: hit.id || parseInt(hit.objectID) || 0,
            title,
            price: hit.price || 0,
            image_url: hit.image_url || null,
            category: hit.category || '',
          };
        });

        setSearchResults(results);
        setShowSearchDropdown(true);
      } catch (error: any) {
        console.error('Navbar search error:', error?.message || error);
        setSearchResults([]);
        if (searchQuery.length >= 2) {
          setShowSearchDropdown(true);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      getResults();
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSearchSubmit = (searchTerm?: string) => {
    const term = searchTerm || searchQuery;
    if (term.trim()) {
      // Get zipcode from localStorage if available (set from homepage)
      const savedZipCode = loadHomeDeliveryLocation()?.zip;
      const searchUrl = savedZipCode 
        ? `/search?q=${encodeURIComponent(term)}&zip=${savedZipCode}`
        : `/search?q=${encodeURIComponent(term)}`;
      navigate(searchUrl);
      setSearchQuery('');
      setShowSearchDropdown(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    // Strip HTML tags from title for navigation
    const plainTitle = result.title.replace(/<[^>]*>/g, '');
    // Get zipcode from localStorage if available (set from homepage)
    const savedZipCode = localStorage.getItem('userZipCode');
    const searchUrl = savedZipCode 
      ? `/search?q=${encodeURIComponent(plainTitle)}&zip=${savedZipCode}`
      : `/search?q=${encodeURIComponent(plainTitle)}`;
    navigate(searchUrl);
    setSearchQuery('');
    setShowSearchDropdown(false);
    setShowMobileOverlay(false);
  };

  const toggleMobileOverlay = () => {
    setShowMobileOverlay(!showMobileOverlay);
    if (!showMobileOverlay) {
      // Focus input when opening overlay
      setTimeout(() => {
        mobileSearchInputRef.current?.focus();
      }, 100);
    }
  };

  const handleMobileSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Get zipcode from localStorage if available (set from homepage)
      const savedZipCode = loadHomeDeliveryLocation()?.zip;
      const searchUrl = savedZipCode 
        ? `/search?q=${encodeURIComponent(searchQuery)}&zip=${savedZipCode}`
        : `/search?q=${encodeURIComponent(searchQuery)}`;
      navigate(searchUrl);
      setSearchQuery('');
      setShowMobileOverlay(false);
    }
  };

  // Load notification counts
  useEffect(() => {
    if (!isAuthenticated) {
      prevUnreadRef.current = null;
      return;
    }

    const loadNotifications = async () => {
      try {
        // Load unread messages count
        const unreadData = await messagesAPI.getUnreadCount();
        const unread = unreadData.count || 0;
        setUnreadMessagesCount(unread);
        if (
          prevUnreadRef.current !== null &&
          unread > prevUnreadRef.current &&
          location.pathname !== '/messages'
        ) {
          toast.success('New message', { icon: '💬', duration: 4000 });
        }
        prevUnreadRef.current = unread;

        // Load delivered orders that need rating (for buyers)
        try {
          const orders = await purchasesAPI.getMyOrders();
            const deliveredNotRated = orders.filter((o: any) => 
              o.status === 'delivered' && 
              (!o.is_rated || o.is_rated === 0) && 
              (!o.is_store_rated || o.is_store_rated === 0)
            );
            setDeliveredOrdersCount(deliveredNotRated.length);
        } catch (err) {
          console.error('Error loading orders:', err);
        }
      } catch (err) {
        console.error('Error loading notifications:', err);
      }
    };

    loadNotifications();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, location.pathname]);


  const showNavDelivery = shouldShowNavDelivery(location.pathname);

  return (
    <>
      <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 backdrop-blur-sm bg-white/95">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        <div className={`${showNavDelivery ? 'flex flex-col gap-2' : ''}`}>
        <div className="flex items-center justify-between gap-2 sm:gap-3 min-w-0">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4 md:gap-5">
            <a 
              href="/"
              className="hover:opacity-80 transition-opacity cursor-pointer shrink-0"
              onClick={(e) => {
                console.log('Logo clicked, navigating to home');
                e.preventDefault();
                if (location.pathname === '/') {
                  window.location.reload();
                } else {
                  navigate('/', { replace: true });
                  setTimeout(() => {
                    if (window.location.pathname !== '/') {
                      window.location.href = '/';
                    }
                  }, 50);
                }
              }}
            >
              {showNavDelivery ? (
                <>
                  <Logo size="sm" className="sm:hidden" showTagline={false} />
                  <Logo size="md" className="hidden sm:flex" showTagline={false} />
                </>
              ) : (
                <Logo size="md" showTagline />
              )}
            </a>
            {showNavDelivery && (
              <div className="hidden sm:block min-w-0 shrink pl-0.5 sm:pl-2 md:pl-3">
                <NavDeliveryChip
                  label={deliveryLabel}
                  onClick={() => setShowLocationModal(true)}
                />
              </div>
            )}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 sm:space-x-4 shrink-0">
            {isAuthenticated ? (
              <>
                  <Link
                    to="/sell"
                    className="bg-green-500 hover:bg-green-600 text-white px-4 sm:px-5 py-2 rounded-full font-semibold transition-all text-sm shadow-md hover:shadow-lg hover:scale-105 hidden sm:flex items-center gap-2"
                  >
                    <i className="ri-shopping-bag-3-line text-lg" aria-hidden />
                    Sell
                  </Link>
                  <div className="flex sm:hidden items-center gap-1.5 shrink-0" aria-label="Sell">
                    <Link
                      to="/sell"
                      className="text-xs font-semibold text-green-600 px-2 py-1.5 rounded-lg hover:bg-green-50"
                    >
                      Sell
                    </Link>
                  </div>
                <div className="relative hidden sm:block" ref={searchDropdownRef}>
                  <div 
                    ref={searchWrapperRef}
                    className={`search-wrapper ${isSearchWrapperActive ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSearchWrapperActive(true);
                      setIsSearchExpanded(true);
                      setTimeout(() => {
                        searchInputRef.current?.focus();
                      }, 100);
                    }}
                  >
                    <i className="ri-search-line search-icon"></i>
                    <span className="search-text">Search</span>
                    <input
                      ref={searchInputRef}
                      type="text"
                      id="searchInput"
                      className="search-input"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        if (e.target.value.length >= 2) {
                          setShowSearchDropdown(true);
                        } else {
                          setShowSearchDropdown(false);
                        }
                      }}
                      onFocus={() => {
                        if (searchQuery.length >= 2) {
                          setShowSearchDropdown(true);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSearchSubmit();
                        }
                        if (e.key === 'Escape') {
                          setShowSearchDropdown(false);
                          setIsSearchWrapperActive(false);
                          setIsSearchExpanded(false);
                          setSearchQuery('');
                        }
                      }}
                      placeholder="Search..."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.innerWidth < 640) {
                        toggleMobileOverlay();
                      }
                    }}
                    className="sm:hidden text-gray-700 hover:text-gray-900 transition-all duration-200 text-sm font-medium flex items-center space-x-1.5 p-2.5 rounded-full hover:bg-gray-100 active:scale-95"
                  >
                    <i className="ri-search-line text-xl"></i>
                    <span className="font-medium">Search</span>
                  </button>
                  {isSearchWrapperActive && searchQuery.length >= 2 && (
                    <div 
                      className="absolute z-[100] mt-2 bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-[500px] overflow-y-auto top-full"
                      style={{
                        width: '300px',
                        maxWidth: '300px',
                        display: 'block',
                        visibility: 'visible',
                        opacity: 1,
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        backgroundColor: 'white',
                        zIndex: 1000
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {searchResults.length > 0 ? (
                        <div className="py-2">
                          {searchResults.map((result, index) => (
                            <button
                              key={result.objectID || result.id}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleResultClick(result);
                              }}
                              className="item w-full text-left px-5 py-3.5 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors cursor-pointer flex items-center space-x-4 border-b border-gray-100 last:border-b-0 group"
                            >
                              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center transition-colors">
                                <i className="ri-search-line text-gray-500 text-base"></i>
                              </div>
                              <div 
                                className="flex-1 text-gray-900 text-sm font-medium"
                                dangerouslySetInnerHTML={{ __html: result.title }}
                              />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="px-4 py-12 text-center">
                          <i className="ri-search-line text-4xl text-gray-300 mb-3"></i>
                          <p className="text-gray-500 text-sm font-medium">No results found</p>
                          <p className="text-gray-400 text-xs mt-1">Try different keywords</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Link to="/cart" className="hidden sm:flex text-gray-700 hover:text-gray-900 transition-all text-sm font-medium items-center space-x-1 p-2 rounded-lg hover:bg-gray-100 relative">
                  {getTotalItems() > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-green-500 rounded-full shadow-md">
                      {getTotalItems()}
                    </span>
                  )}
                  <i className="ri-shopping-cart-line text-lg sm:text-xl"></i>
                  <span className="hidden sm:inline">Cart</span>
                </Link>
                <Link 
                  to="/my-orders" 
                  onClick={() => setDeliveredOrdersCount(0)}
                  className="hidden sm:flex text-gray-700 hover:text-gray-900 transition-all text-sm font-medium items-center space-x-1 p-2 rounded-lg hover:bg-gray-100 relative"
                >
                  {deliveredOrdersCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full shadow-md">
                      {deliveredOrdersCount}
                    </span>
                  )}
                  <i className="ri-shopping-bag-line text-lg sm:text-xl"></i>
                  <span className="hidden sm:inline">Orders</span>
                </Link>
                <Link to="/messages" className="hidden sm:flex items-center text-gray-700 hover:text-gray-900 transition-all text-sm font-medium relative p-2 rounded-lg hover:bg-gray-100">
                  {unreadMessagesCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full shadow-md">
                      {unreadMessagesCount}
                    </span>
                  )}
                  <i className="ri-message-line text-lg sm:text-xl"></i>
                  <span className="hidden sm:inline ml-1">Messages</span>
                </Link>
                
                {/* User Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all text-sm font-medium text-gray-700 relative"
                    aria-expanded={dropdownOpen}
                    aria-haspopup="true"
                    aria-label={`Account menu (${user?.username || 'user'})`}
                  >
                    <span className="hidden sm:inline truncate max-w-[9rem]">{user?.username}</span>
                    <span className="sm:hidden">{user?.username?.substring(0, 1).toUpperCase()}</span>
                    {accountAlertCount > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[1.125rem] px-1 py-0.5 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full shadow-md">
                        {accountAlertCount > 99 ? '99+' : accountAlertCount}
                      </span>
                    )}
                    <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} aria-hidden />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                      <Link
                        to="/messages"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-all rounded-lg mx-1"
                      >
                        <div className="flex items-center space-x-2">
                          <i className="ri-message-3-line"></i>
                          <span>Messages</span>
                        </div>
                        {unreadMessagesCount > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                            {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                          </span>
                        )}
                      </Link>
                      <Link
                        to="/cart"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-all rounded-lg mx-1"
                      >
                        <div className="flex items-center space-x-2">
                          <i className="ri-shopping-cart-line"></i>
                          <span>Cart</span>
                        </div>
                        {getTotalItems() > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-green-600 rounded-full">
                            {getTotalItems() > 99 ? '99+' : getTotalItems()}
                          </span>
                        )}
                      </Link>
                      <Link
                        to="/my-orders"
                        onClick={() => {
                          setDeliveredOrdersCount(0);
                          setDropdownOpen(false);
                        }}
                        className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-all rounded-lg mx-1"
                      >
                        <div className="flex items-center space-x-2">
                          <i className="ri-shopping-bag-line"></i>
                          <span>Orders</span>
                        </div>
                        {deliveredOrdersCount > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                            {deliveredOrdersCount > 99 ? '99+' : deliveredOrdersCount}
                          </span>
                        )}
                      </Link>
                      <Link
                        to="/settings#addresses"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-all rounded-lg mx-1"
                      >
                        <i className="ri-map-pin-line"></i>
                        <span>Addresses</span>
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-all rounded-lg mx-1"
                      >
                        <i className="ri-settings-line"></i>
                        <span>Settings</span>
                      </Link>
                      <Link
                        to="/sell"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-green-600 hover:bg-green-50 transition-all rounded-lg mx-1"
                      >
                        <i className="ri-shopping-bag-3-line text-base" aria-hidden />
                        Seller app
                      </Link>
                      <div className="border-t border-gray-200 my-2"></div>
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-2 w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-all rounded-lg mx-1"
                      >
                        <i className="ri-logout-box-line"></i>
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="relative hidden sm:block" ref={searchDropdownRef}>
                  <div 
                    ref={searchWrapperRef}
                    className={`search-wrapper ${isSearchWrapperActive ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSearchWrapperActive(true);
                      setIsSearchExpanded(true);
                      setTimeout(() => {
                        searchInputRef.current?.focus();
                      }, 100);
                    }}
                  >
                    <i className="ri-search-line search-icon"></i>
                    <span className="search-text">Search</span>
                    <input
                      ref={searchInputRef}
                      type="text"
                      id="searchInput"
                      className="search-input"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        if (e.target.value.length >= 2) {
                          setShowSearchDropdown(true);
                        } else {
                          setShowSearchDropdown(false);
                        }
                      }}
                      onFocus={() => {
                        if (searchQuery.length >= 2) {
                          setShowSearchDropdown(true);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSearchSubmit();
                        }
                        if (e.key === 'Escape') {
                          setShowSearchDropdown(false);
                          setIsSearchWrapperActive(false);
                          setIsSearchExpanded(false);
                          setSearchQuery('');
                        }
                      }}
                      placeholder="Search..."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.innerWidth < 640) {
                        toggleMobileOverlay();
                      }
                    }}
                    className="sm:hidden text-gray-700 hover:text-gray-900 transition-all duration-200 text-sm font-medium flex items-center space-x-1.5 p-2.5 rounded-full hover:bg-gray-100 active:scale-95"
                  >
                    <i className="ri-search-line text-xl"></i>
                    <span className="font-medium">Search</span>
                  </button>
                  {isSearchWrapperActive && searchQuery.length >= 2 && (
                    <div 
                      className="absolute z-[100] mt-2 bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-[500px] overflow-y-auto top-full"
                      style={{
                        width: '300px',
                        maxWidth: '300px',
                        display: 'block',
                        visibility: 'visible',
                        opacity: 1,
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        backgroundColor: 'white',
                        zIndex: 1000
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {searchResults.length > 0 ? (
                        <div className="py-2">
                          {searchResults.map((result, index) => (
                            <button
                              key={result.objectID}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleResultClick(result);
                              }}
                              className="item w-full text-left px-5 py-3.5 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors cursor-pointer flex items-center space-x-4 border-b border-gray-100 last:border-b-0 group"
                            >
                              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center transition-colors">
                                <i className="ri-search-line text-gray-500 text-base"></i>
                              </div>
                              <div 
                                className="flex-1 text-gray-900 text-sm font-medium"
                                dangerouslySetInnerHTML={{ __html: result.title }}
                              />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="px-4 py-12 text-center">
                          <i className="ri-search-line text-4xl text-gray-300 mb-3"></i>
                          <p className="text-gray-500 text-sm font-medium">No results found</p>
                          <p className="text-gray-400 text-xs mt-1">Try different keywords</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Link
                  to="/sell"
                  className="text-gray-700 hover:text-gray-900 transition-all text-sm font-medium px-3 py-2 rounded-lg hover:bg-gray-100 hidden sm:inline-flex items-center gap-1.5"
                >
                  <i className="ri-shopping-bag-3-line text-base" aria-hidden />
                  Sell
                </Link>
                {/* Log in + Sign up grouped (desktop) */}
                <div className="hidden sm:flex items-center gap-2 shrink-0" aria-label="Account">
                  <Link
                    to="/login"
                    className="text-gray-700 hover:text-gray-900 transition-all text-sm font-semibold px-4 py-2 rounded-full hover:bg-gray-50"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/register"
                    className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-full font-semibold transition-all text-sm shadow-md hover:shadow-lg hover:scale-105"
                  >
                    Sign up
                  </Link>
                </div>
                {/* Mobile: Sell + Log in + Sign up in one row */}
                <div className="flex sm:hidden items-center gap-1.5 shrink-0 mr-0.5" aria-label="Sell, log in, and sign up">
                  <Link
                    to="/sell"
                    className="text-xs font-semibold text-green-600 px-2 py-1.5 rounded-lg hover:bg-green-50"
                  >
                    Sell
                  </Link>
                  <Link
                    to="/login"
                    className="text-xs font-semibold text-gray-800 px-2.5 py-1.5 rounded-full hover:bg-gray-50"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/register"
                    className="text-xs font-semibold text-white bg-green-500 hover:bg-green-600 px-3 py-1.5 rounded-full shadow-sm"
                  >
                    Sign up
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
        {showNavDelivery && (
          <div className="sm:hidden w-fit max-w-full min-w-0">
            <NavDeliveryChip
              label={deliveryLabel}
              onClick={() => setShowLocationModal(true)}
            />
          </div>
        )}
        </div>
      </div>
    </nav>
      
      {/* Floating Action Button for Mobile - Only on Home Page */}

      {/* Mobile Search Overlay */}
      <div 
        className={`overlay fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300 sm:hidden ${
          showMobileOverlay ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={toggleMobileOverlay}
      >
        <div 
          className="overlay-content bg-white h-full w-full p-4 transform transition-transform duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          <form onSubmit={handleMobileSearchSubmit} className="flex flex-col h-full">
            <div className="flex items-center space-x-3 mb-4">
              <button
                type="button"
                onClick={toggleMobileOverlay}
                className="closebtn text-gray-700 hover:text-gray-900 p-2"
                title="Close Overlay"
              >
                <i className="ri-arrow-left-line text-2xl"></i>
              </button>
              <input
                ref={mobileSearchInputRef}
                type="text"
                placeholder="Search.."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                name="search"
                autoComplete="off"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
              />
              <button
                type="submit"
                className="text-gray-700 hover:text-gray-900 p-2"
              >
                <i className="ri-search-line text-2xl"></i>
              </button>
            </div>
            
            {searchQuery && searchResults.length > 0 && (
              <div className="search__result flex-1 overflow-y-auto">
                {searchResults.map((result) => (
                  <button
                    key={result.objectID}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleResultClick(result);
                    }}
                    className="item w-full text-left px-4 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors cursor-pointer flex items-center space-x-3 border-b border-gray-100 last:border-b-0"
                  >
                    <div 
                      className="flex-1 text-gray-900 font-medium"
                      dangerouslySetInnerHTML={{ __html: result.title }}
                    />
                    {result.image_url && (
                      <ListingImage
                        src={result.image_url}
                        size="thumb"
                        alt=""
                        className="w-12 h-12 object-cover rounded flex-shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Mobile bottom tab bar — home page only */}
      {isAuthenticated && location.pathname === '/' && (
        <div className="fixed inset-x-0 bottom-0 z-[9999] sm:hidden pointer-events-none overflow-visible">
          <div
            className={`will-change-transform transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] overflow-visible ${
              mobileBottomNavVisible
                ? 'translate-y-0 opacity-100 pointer-events-auto'
                : 'translate-y-[calc(100%+20px)] opacity-0 pointer-events-none'
            }`}
          >
            <div className="max-w-lg mx-auto w-full border-t border-gray-200 bg-white backdrop-blur-sm shadow-[0_-4px_12px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom,0px)] overflow-visible">
              <nav aria-label="Main navigation" className="overflow-visible">
                <div className="relative flex items-stretch justify-around h-14 overflow-visible">
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center flex-1 min-w-0 gap-0.5 text-[10px] font-medium ${
                      isActive ? 'text-green-600' : 'text-gray-500'
                    }`
                  }
                >
                  <i className="ri-home-5-line text-xl leading-none" />
                  <span>Home</span>
                </NavLink>
                <NavLink
                  to="/search"
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center flex-1 min-w-0 gap-0.5 text-[10px] font-medium ${
                      isActive ? 'text-green-600' : 'text-gray-500'
                    }`
                  }
                >
                  <i className="ri-search-line text-xl leading-none" />
                  <span>Search</span>
                </NavLink>
                <NavLink
                  to="/my-orders"
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center flex-1 min-w-0 gap-0.5 text-[10px] font-medium ${
                      isActive ? 'text-green-600' : 'text-gray-500'
                    }`
                  }
                >
                  <span className="relative inline-flex overflow-visible">
                    {deliveredOrdersCount > 0 && (
                      <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-green-600 rounded-full z-[10000] ring-2 ring-white">
                        {deliveredOrdersCount > 99 ? '99+' : deliveredOrdersCount}
                      </span>
                    )}
                    <i className="ri-shopping-bag-line text-xl leading-none" />
                  </span>
                  <span>Orders</span>
                </NavLink>
                <NavLink
                  to="/messages"
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center flex-1 min-w-0 gap-0.5 text-[10px] font-medium ${
                      isActive ? 'text-green-600' : 'text-gray-500'
                    }`
                  }
                >
                  <span className="relative inline-flex overflow-visible">
                    {unreadMessagesCount > 0 && (
                      <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full z-[10000] ring-2 ring-white">
                        {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                      </span>
                    )}
                    <i className="ri-message-3-line text-xl leading-none" />
                  </span>
                  <span>Messages</span>
                </NavLink>
                <NavLink
                  to="/settings"
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center flex-1 min-w-0 gap-0.5 text-[10px] font-medium ${
                      isActive ? 'text-green-600' : 'text-gray-500'
                    }`
                  }
                >
                  <span className="relative inline-flex overflow-visible">
                    {accountAlertCount > 0 && (
                      <span
                        className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full z-[10000] ring-2 ring-white"
                        title="Messages or offer alerts"
                      >
                        {accountAlertCount > 99 ? '99+' : accountAlertCount}
                      </span>
                    )}
                    <i className="ri-user-3-line text-xl leading-none" />
                  </span>
                  <span>Profile</span>
                </NavLink>
              </div>
              </nav>
            </div>
          </div>
        </div>
      )}
      {showNavDelivery && showLocationModal ? (
        <Suspense fallback={null}>
          <HomeLocationModal
            isOpen={showLocationModal}
            onClose={() => setShowLocationModal(false)}
            onLocationSelect={async (cityId, stateId, cityName, stateName, fullAddress) => {
              await saveDeliveryLocationFromModal(cityName, stateName, fullAddress, cityId, stateId);
              setShowLocationModal(false);
            }}
          />
        </Suspense>
      ) : null}
    </>
  );
};

export default Navbar;

